import type { ComponentType } from 'preact';
import { cx } from './internal';

interface VMetricCardProps {
  /** Optional lucide-style icon component rendered top-left.
   *  Size is typed as `number | string` to match lucide-preact's
   *  LucideIcon surface — the component invokes it with a number. */
  icon?: ComponentType<{ size?: number | string }>;
  /** Short label above the value — e.g. "Revenue", "Steps", "Sleep". */
  label: string;
  /** The hero number. String allowed for pre-formatted values ("$12.4k"). */
  value: string | number;
  /** Delta string — e.g. "+12.4%", "-3", "0%". Rendered next to the value. */
  delta?: string;
  /** Trend direction — drives the delta's color tint. */
  deltaTrend?: 'up' | 'down' | 'flat';
  /** Optional inline sparkline data — rendered as a 32px SVG at the bottom. */
  sparkline?: number[];
  className?: string;
}

const TREND = {
  up: 'text-emerald-600 dark:text-emerald-400',
  down: 'text-rose-600 dark:text-rose-400',
  flat: 'text-[var(--color-fg-muted)]' // A2 — flat trend uses the blueprint muted ink
} as const;

const SPARK_HEIGHT = 32;
const SPARK_WIDTH = 120;

/**
 * High-density KPI card. Render with a single line:
 *
 *   <VMetricCard icon={DollarSign} label="MRR" value="$12.4k" delta="+8.2%" deltaTrend="up" sparkline={[1,3,2,5,4,7,6]} />
 *
 * Encapsulates a fair amount of layout (icon square, label, hero
 * number, delta pill, sparkline) so the LLM doesn't have to spell out
 * Tailwind for every dashboard tile.
 */
export function VMetricCard({
  icon: Icon,
  label,
  value,
  delta,
  deltaTrend = 'flat',
  sparkline,
  className
}: VMetricCardProps) {
  return (
    <div
      // A2 — surface from the blueprint card token. Unlike VCard there is no
      // data-vs-card blueprint override here, so this is a genuine palette-blind
      // surface that must read the token directly.
      style={{ background: 'var(--color-bg-alt, #ffffff)' }}
      className={cx(
        // `min-w-0` lets the card collapse cleanly inside a grid/flex parent.
        'flex flex-col gap-3 rounded-2xl border border-black/[0.06] p-4 min-w-0 overflow-hidden',
        'shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_12px_-2px_rgba(0,0,0,0.06)]',
        'dark:border-white/[0.08] dark:shadow-[0_1px_2px_rgba(0,0,0,0.4),0_4px_12px_-2px_rgba(0,0,0,0.5)]',
        className
      )}
    >
      <div className="flex items-start justify-between gap-2 min-w-0">
        {Icon ? (
          <div
            className={cx(
              // Pink-tinted icon square — uses the design-system
              // primary-muted token so it auto-themes.
              'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[var(--color-primary-muted)] text-[var(--color-primary-active)]'
            )}
          >
            <Icon size={18} />
          </div>
        ) : null}
        <div className="flex flex-1 flex-col gap-0.5 min-w-0">
          <span className="text-xs font-medium uppercase tracking-wide text-[var(--color-fg-muted)] truncate">
            {label}
          </span>
        </div>
      </div>

      <div className="flex items-baseline gap-2 min-w-0">
        <span className="text-2xl font-semibold tabular-nums tracking-tight text-[var(--color-fg)] truncate">
          {value}
        </span>
        {delta ? (
          <span className={cx('text-xs font-semibold tabular-nums shrink-0', TREND[deltaTrend])}>
            {delta}
          </span>
        ) : null}
      </div>

      {sparkline && sparkline.length > 1 ? (
        <Sparkline data={sparkline} trend={deltaTrend} />
      ) : null}
    </div>
  );
}

function Sparkline({ data, trend }: { data: number[]; trend: 'up' | 'down' | 'flat' }) {
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const stepX = SPARK_WIDTH / (data.length - 1);

  // Build a smooth polyline by sampling each point. Y-axis is
  // inverted (SVG origin top-left) so higher values are drawn higher.
  const points = data
    .map((v, i) => {
      const x = i * stepX;
      const y = SPARK_HEIGHT - ((v - min) / range) * SPARK_HEIGHT;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');

  // Build the area-fill path under the line.
  const areaPath = (() => {
    const linePts = data.map((v, i) => {
      const x = i * stepX;
      const y = SPARK_HEIGHT - ((v - min) / range) * SPARK_HEIGHT;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    });
    return `M0,${SPARK_HEIGHT} L${linePts.join(' L')} L${SPARK_WIDTH},${SPARK_HEIGHT} Z`;
  })();

  const stroke =
    trend === 'down'
      ? 'rgb(225 29 72)' // rose-600
      : trend === 'flat'
      ? 'rgb(115 115 115)' // neutral-500
      : 'rgb(5 150 105)'; // emerald-600

  return (
    <svg
      role="img"
      aria-label={`Trend sparkline, ${data.length} points`}
      viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`}
      className="w-full"
      style={{ height: SPARK_HEIGHT }}
      preserveAspectRatio="none"
    >
      <path d={areaPath} fill={stroke} fillOpacity={0.12} />
      <polyline
        points={points}
        fill="none"
        stroke={stroke}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
